// ----- Raw server types (as returned by backend) -----
export type RawTenant = {
  id: string;
  name: string;
  domain: string;
  lastScanned: string;
  lastDeployed: string;
  deployer?: string | null;
  status: 'Available' | 'Not Available' | 'Error';
};

export type RawSit = {
  id: number;
  name: string;
  category: string;
  confidence: 'High' | 'Medium' | 'Low';
  tenantCoverage: string; // e.g. "4/6"
  lastScanned: string;
  tenants?: RawTenant[];
};

export type RawLabel = {
  id: number;
  name: string;
  category: string;
  protectionLevel: 'None' | 'Basic' | 'Advanced' | 'Maximum';
  tenantCoverage: string;
  lastScanned: string;
  tenants?: RawTenant[];
};

export type RawPolicy = {
  id: number;
  name: string;
  category: string;
  priority: 'High' | 'Medium' | 'Low' | string;
  tenantCoverage: string;
  lastScanned: string;
  tenants?: RawTenant[];
};

// ----- Tenant-specific entity types -----
export type RawTenantEntity = {
  entityId: string;
  entityName: string;
  category: {
    value: string;
  };
  protectionLevel: string;
  confidence: 'High' | 'Medium' | 'Low';
  status: string;
  lastDeployed: string;
  deployedStatus: string;
  isDeployed: boolean;
  isHighConfidence: boolean;
};

export type TenantEntityResponse = {
  entities: RawTenantEntity[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
};

export type TenantEntityError = {
  error: string;
  message: string;
  timestamp: string;
  details?: {
    tenantId?: string;
  };
};

// ----- Dashboard entity types -----
export type DashboardEntity = {
  entityId: string;
  entityName: string;
  category: string;
  confidence: string;
  lastRefreshed: string;
  protectionLevel: string;
  tenantCoverage: string;
  totalPassedTenants: number;
  totalDeployedTenants: number;
  deploymentPercentage: number;
  deployedTenantsList: string[];
  isFullyDeployed: boolean;
};

export type DashboardEntityResponse = {
  entities: DashboardEntity[];
  totalAuthorizedTenants: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
};

// ----- Entity Metadata API types -----
export type EntityMetadataRequest = {
  entityId: string;
  entityType: string;
  tenantIds: string[];
};

export type EntityMetadataItem = {
  entityId: string;
  entityName: string;
  entityType: string;
  tenantId: string;
  tenantName: string;
  lastDeployedTime: string;
  deployedBy: string;
  deploymentStatus: string;
  category: string;
  confidence: string;
  protectionLevel: string;
  lastRefreshed: string;
};

export type EntityMetadataResponse = {
  entities: EntityMetadataItem[];
  entityName: string;
  entityType: string;
  totalTenants: number;
};

// ----- Deployment API types -----
/**
 * Single entity item for deployment with optional data payload
 * Note: 'data' field is optional and currently omitted from requests
 */
export type DeployEntityItem = {
  id: string;
  data?: Record<string, unknown> | null;
};

/**
 * Request to deploy entities to tenants
 * Supports one-to-many OR many-to-one (not many-to-many)
 */
export type DeployRequest = {
  entityType: string;
  entityIds: DeployEntityItem[];
  tenantIds: string[];
};

/**
 * Response after deployment request is accepted
 */
export type DeployResponse = {
  requestId: string;
  status: string;
};

// ----- Label Deployment Coverage API types (GET /entities/labels/deployment-coverage) -----
// Single shape powers both dashboard view (no targetTenantIds query param)
// and tenant view (single targetTenantIds value).

export type LabelDeploymentStatus = 'Deployed' | 'NotDeployed';

export interface LabelSourceMetadata {
  createdDateTime: string | null;
  lastModifiedDateTime: string | null;
  extractedDateTime: string | null;
  processedDateTime: string | null;
}

export interface LabelData {
  name: string;
  displayName: string;
  description: string | null;
  parentId: string | null;
  isParent: boolean;
  isLabelGroup: boolean;
  priority: number;
  parentLabelDisplayName: string | null;
  disabled: boolean;
  mode: string | null;
  workload: string | null;        // CSV string from payload, rendered as-is
  contentType: string | null;     // CSV string from payload, rendered as-is
  metadata: LabelSourceMetadata;
}

export interface LabelTenantDeployment {
  tenantId: string;
  deploymentStatus: LabelDeploymentStatus;
  lastDeployedDateTime: string | null;
  // Per-tenant deployed names read from purviewinventory (already prefixed if the DE applied a prefix).
  // Null for not-deployed tenants. Read-only — the UI never computes these.
  deployedDisplayName?: string | null;
  deployedParentLabelDisplayName?: string | null;
}

export interface LabelEntity {
  entityId: string;
  data: LabelData;
  tenantCoverage: string;            // e.g. "1/2"
  deployedTenantsCount: number;
  tenantDeployments: LabelTenantDeployment[];
}

// ========== Shared Deployment Types ==========
// These are entity-agnostic (used by labels, policies, SITs) and live here
// rather than in the policy-specific types file.

export type DeploymentStatus = 'Deployed' | 'NotDeployed' | 'PartiallyDeployed' | 'InProgress' | 'DeploymentFailed';

/** Top-level entity type for deployment requests */
export type BffEntityType = 'SIT' | 'LABEL' | 'POLICY';

/** Per-tenant deployment target with optional rule data */
export interface BffDeploymentTarget {
  tenantId: string;
  data?: Record<string, string[]>;
}

/** Entity within a deployment request */
export interface BffEntityDetail {
  id: string;
  type?: string;
  targets: BffDeploymentTarget[];
}

/** POST /api/deployments request body */
export interface BffDeploymentRequest {
  entityType: BffEntityType;
  entities: BffEntityDetail[];
}

/** Single item in accepted/failed/conflicts arrays */
export interface BffDeploymentItem {
  deploymentId: string | null;
  deploymentStatus: string | null;
  entityType: string | null;
  entityId: string | null;
  targetTenantId: string | null;
  reason: string | null;
}

/** POST /api/deployments response (202 Accepted) */
export interface BffDeploymentResponse {
  requestId: string;
  status: string;
  batchId: string | null;
  accepted: BffDeploymentItem[];
  failed: BffDeploymentItem[];
  conflicts: BffDeploymentItem[];
}

/** Single entity item in the GET status response */
export interface BffDeploymentEntityItem {
  name: string;
  tenantId: string;
  status: string | null;
  deploymentId: string | null;
  warnings?: BffDeploymentWarning[] | null;
}

/** A prefix/redeploy warning as returned on the wire by the BFF (camelCase) */
export interface BffDeploymentWarning {
  label: string | null;
  message: string | null;
}

/** A deployment request status from GET /api/deployments */
export interface BffDeploymentRequestStatus {
  id: string;
  entityType: string;
  entityItems: BffDeploymentEntityItem[];
  tenantIds: string[];
  createdAt: string;
  status: string | null;
  requestedBy: string | null;
  rejectionReason: string | null;
  failureReason: string | null;
}

/** GET /api/deployments response */
export interface BffDeploymentStatusListResponse {
  requests: BffDeploymentRequestStatus[];
  totalCount: number;
}

/** Result of a deployment attempt for UI consumption */
export interface DeploymentResult {
  success: boolean;
  batchId?: string;
  acceptedCount: number;
  failedCount: number;
  conflictCount: number;
  message: string;
  statusUpdates: Map<string, DeploymentStatus>;
  acceptedItems: Array<{
    deploymentId: string;
    tenantId: string;
    entityId: string;
  }>;
}

/** Terminal statuses — stop polling when reached */
export const TERMINAL_DEPLOYMENT_STATUSES = new Set([
  'Completed',
  'Failed',
]);

/** All known BFF deployment statuses */
export type ApiDeploymentStatus = 'Pending' | 'Received' | 'Completed' | 'Failed';

/** Map API status string to the DeploymentStatus type used in UI */
export function mapApiStatusToDeploymentStatus(apiStatus: string | null | undefined): DeploymentStatus {
  switch (apiStatus) {
    case 'Completed':
      return 'Deployed';
    case 'Failed':
      return 'DeploymentFailed';
    case 'Pending':
    case 'Received':
    default:
      return 'InProgress';
  }
}

/** A tracked deployment item being polled */
export interface TrackedDeployment {
  deploymentId: string;
  tenantId: string;
  entityId: string;
  policyName: string;
  entityType?: 'POLICY' | 'LABEL' | 'SIT';
  view: 'dashboard' | 'tenant';
  apiStatus: string;
  startedAt: number;
  lastPolledAt: number;
  errorMessage?: string;
}

/** A non-blocking deployment warning surfaced to the user (e.g. prefix skip on redeploy) */
export interface DeploymentWarning {
  label: string;
  message: string;
}

/** A surfaced warning retained for banner display until the user dismisses it */
export interface DeploymentWarningEntry extends DeploymentWarning {
  /** Unique key for dismissal: `${deploymentId}::${label}` */
  id: string;
  deploymentId: string;
  tenantId: string;
  policyName: string;
}

/** localStorage key for persisting tracked deployments */
export const DEPLOYMENT_TRACKING_STORAGE_KEY = 'purview_deployment_tracking';

/** Polling interval in milliseconds (15 seconds) */
export const DEPLOYMENT_POLL_INTERVAL_MS = 15_000;

/** Max polling duration in milliseconds (7 minutes) */
export const DEPLOYMENT_POLL_MAX_DURATION_MS = 7 * 60 * 1000;

/** Deployment status filter options — shared across entity tables */
export const STATUS_OPTIONS = [
  { key: 'All', label: 'All Statuses' },
  { key: 'Deployed', label: 'Available' },
  { key: 'NotDeployed', label: 'Not Available' },
  { key: 'PartiallyDeployed', label: 'Partially Deployed' },
  { key: 'InProgress', label: 'In Progress' },
  { key: 'DeploymentFailed', label: 'Failed' },
] as const;

export interface LabelDeploymentCoverageResponse {
  baseTenantId: string;
  baseTenantName: string;
  entityType: 'Label';
  totalTenants: number;
  entities: LabelEntity[];
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
  };
}

// ----- UI types (what the app uses). Aliased for now for easy future changes -----
export type UITenant = RawTenant;
export type UISit = RawSit;
export type UILabel = RawLabel;
export type UIPolicy = RawPolicy;
export type UITenantEntity = RawTenantEntity;
